import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import { $tk } from '@/_translate';
import { VTN } from '@/store/types';

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer.vue');

// Views
const Dashboard = () => import('@/views/Pages/Dashboard.vue');

// Views - Pages
const Page404 = () => import('@/views/Pages/Page404.vue');
const Page500 = () => import('@/views/Pages/Page500.vue');
const Login = () => import('@/views/Pages/Login.vue');
const Register = () => import('@/views/Pages/Register.vue');
const PasswordForgot = () => import('@/views/Pages/PasswordReset/PasswordForgot.vue');
const PasswordReset = () => import('@/views/Pages/PasswordReset/PasswordReset.vue');

// Views - Administration
const Users = () => import('@/views/Administration/Users/Users.vue');
const UserCreate = () => import('@/views/Administration/Users/UserCreate.vue');
const UserUpdate = () => import('@/views/Administration/Users/UserUpdate.vue');
const UserProfile = () => import('@/views/UserProfile/UserProfile.vue');
const Roles = () => import('@/views/Administration/Roles/Roles.vue');
const RoleCreate = () => import('@/views/Administration/Roles/RoleCreate.vue');
const RoleUpdate = () => import('@/views/Administration/Roles/RoleUpdate.vue');
const AuditLogs = () => import('@/views/Administration/AuditLogs.vue');
const Notifications = () => import('@/views/Administration/Notifications/Notifications.vue');

const ApiTokenForm = () => import('@/views/UserProfile/ApiTokenForm.vue');

const Translations = () => import('@/views/Administration/Translations/Translations.vue');
const TranslationForm = () => import('@/views/Administration/Translations/TranslationForm.vue');

const Languages = () => import('@/views/Administration/Languages/Languages.vue');
const LanguageForm = () => import('@/views/Administration/Languages/LanguageForm.vue');

// GenCrud.0.stubpart (stub fragment will be generated bellow this line - do not delete this)

const JsonSchemas = () => import('@/views/JsonSchemas/JsonSchemas');
const JsonSchemaForm = () => import('@/views/JsonSchemas/JsonSchemaForm');

const DetectionSets = () => import('@/views/DetectionSets/DetectionSets.vue');
const DetectionSetForm = () => import('@/views/DetectionSets/DetectionSetForm.vue');
const DetectionSetDelete = () => import('@/views/DetectionSets/DetectionSetDelete.vue');
const DetectionSetUpload = () => import('@/views/DetectionSets/DetectionSetUpload.vue');

const Detections = () => import('@/views/Detections/Detections.vue');
const DetectionForm = () => import('@/views/Detections/DetectionForm.vue');

const TimeLogs = () => import('@/views/TimeLogs/TimeLogs.vue');

const ButtonPagination = () => import('@/views/LabelObjectSets/ButtonPagination.vue');

const AiJobs = () => import('@/views/AiJobs/AiJobs.vue');
const AiJobForm = () => import('@/views/AiJobs/AiJobForm.vue');

const AiModels = () => import('@/views/AiModels/AiModels.vue');
const AiModelForm = () => import('@/views/AiModels/AiModelForm.vue');

// const LabelSetTags = () => import('@/views/LabelSetTags/LabelSetTags');
// const LabelSetTagForm = () => import('@/views/LabelSetTags/LabelSetTagForm');

const LabelSets = () => import('@/views/LabelSets/LabelSets.vue');
const LabelSetForm = () => import('@/views/LabelSets/LabelSetForm.vue');
const LabelSetSamples = () => import('@/views/LabelSets/LabelSetSamples.vue');
const CodeSnippets = () => import('@/views/LabelSets/CodeSnippets.vue');

const Tags = () => import('@/views/Tags/Tags.vue');
const TagForm = () => import('@/views/Tags/TagForm.vue');

const Samples = () => import('@/views/Samples/Samples.vue');
const SampleForm = () => import('@/views/Samples/SampleForm.vue');

const SampleSets = () => import('@/views/SampleSets/SampleSets.vue');
const SampleSetForm = () => import('@/views/SampleSets/SampleSetForm.vue');
const SampleSetUpload = () => import('@/views/SampleSets/SampleSetUpload.vue');

const ProjectUsers = () => import('@/views/ProjectUsers/ProjectUsers.vue');
const ProjectUserForm = () => import('@/views/ProjectUsers/ProjectUserForm.vue');

const Projects = () => import('@/views/Projects/Projects.vue');
const ProjectForm = () => import('@/views/Projects/ProjectForm.vue');
const ProjectExportation = () => import('@/views/Projects/ProjectExportation.vue');

const SampleSetDelete = () => import('@/views/SampleSets/SampleSetDelete.vue'); // need to change this later
Vue.use(Router);

const publicRoutes = [
  'Login',
  'Register',
  'PasswordForgot',
  'PasswordReset',
];

const router = new Router({
  // mode: 'hash', // https://router.vuejs.org/api/#mode
  mode: 'history',
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: '/pages',
      redirect: '/pages/404',
      name: null,
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          meta: { label: $tk('route.error404') },
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          meta: { label: $tk('route.error500') },
          component: Page500
        },
      ]
    },
    {
      path: '/',
      redirect: { name: 'Dashboard' },
      name: null,
      component: DefaultContainer,
      children: [
        // GenCrud.1.stubpart (stub fragment will be generated bellow this line - do not delete this)
        {
          path: 'tags',
          name: 'AllTags',
          meta: { label: $tk('route.tags_list_title') },
          redirect: { name: 'Tags', params: { page: 1 } },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'list/:page?',
              name: 'Tags',
              meta: { label: $tk('route.tag_list') },
              props: (route) => {
                return {
                  page: Number(route.params.page || 1)
                };
              },
              component: Tags,
            },
            {
              path: 'create',
              name: 'TagCreate',
              meta: { label: $tk('route.tag_create_title') },
              component: TagForm
            },
            {
              path: 'update/:id',
              name: 'TagUpdate',
              meta: { label: $tk('route.tag_update_title') },
              props: (route) => {
                return {
                  id: Number(route.params.id)
                };
              },
              component: TagForm
            },
          ]
        },

        {
          path: 'projects/:project_id',
          name: null,
          // meta: { label: $tk('route.app_list') },
          props: (route) => {
            return {
              project_id: Number(route.params.project_id)
            };
          },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'time_logs',
              name: 'AllTimeLogs',
              meta: { label: $tk('route.time_logs_list_title') },
              redirect: '/projects/:project_id/time_logs/list',

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list',
                  name: 'TimeLogs',
                  meta: { label: $tk('route.time_log_list') },
                  props: true,
                  component: TimeLogs,
                },
              ]
            },
            {
              path: 'project_users',
              name: 'AllProjectUsers',
              meta: { label: $tk('route.project_users_list_title') },
              redirect: '/projects/:project_id/project_users/list/1',

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page?',
                  name: 'ProjectUsers',
                  meta: { label: $tk('route.project_user_list') },
                  props: (route) => {
                    return {
                      page: Number(route.params.page || 1)
                    };
                  },
                  component: ProjectUsers,
                },
                {
                  path: 'create',
                  name: 'ProjectUserCreate',
                  meta: { label: $tk('route.project_user_create_title') },
                  component: ProjectUserForm
                },
                {
                  path: 'update/:id',
                  name: 'ProjectUserUpdate',
                  meta: { label: $tk('route.project_user_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: ProjectUserForm
                },
              ]
            },
            {
              path: 'ai_jobs',
              // name: null,
              // meta: { label: $tk('route.ai_jobs_list_title') },
              // redirect: { name: 'AiJobs', params: { page: 1 } },
              name: 'AllProjectAiJobs',
              meta: { label: $tk('route.ai_jobs_list_title') },
              // redirect: { name: 'SampleSets', params: { page: 1 } },
              redirect: '/projects/:project_id/ai_jobs/list/1',

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'AiJobs',
                  meta: { label: $tk('route.ai_job_list') },
                  props: true,
                  component: AiJobs,
                },
                {
                  path: 'create',
                  name: 'AiJobCreate',
                  meta: { label: $tk('route.ai_job_create_title') },
                  component: AiJobForm
                },
                {
                  path: 'update/:id',
                  name: 'AiJobUpdate',
                  meta: { label: $tk('route.ai_job_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: AiJobForm
                },
              ]
            },
            {
              path: 'ai_models',
              // name: null,
              // meta: { label: $tk('route.ai_models_list_title') },
              // redirect: { name: 'AiModels', params: { page: 1 } },
              name: 'AllProjectModels',
              meta: { label: $tk('route.ai_models_list_title') },
              // redirect: { name: 'SampleSets', params: { page: 1 } },
              redirect: '/projects/:project_id/ai_models/list/1',

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page?',
                  name: 'AiModels',
                  meta: { label: $tk('route.ai_model_list') },
                  props: (route) => {
                    return {
                      page: Number(route.params.page || 1)
                    };
                  },
                  component: AiModels,
                },
                {
                  path: 'create',
                  name: 'AiModelCreate',
                  meta: { label: $tk('route.ai_model_create_title') },
                  component: AiModelForm
                },
                {
                  path: 'update/:id',
                  name: 'AiModelUpdate',
                  meta: { label: $tk('route.ai_model_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: AiModelForm
                },
              ]
            },
            {
              path: 'label_sets',
              // name: null,
              // meta: { label: $tk('route.label_sets_list_title') },
              // redirect: { name: 'LabelSets', params: { page: 1 } },

              name: 'AllProjectLabelSets',
              meta: { label: $tk('route.label_sets_list_title') },
              // redirect: { name: 'SampleSets', params: { page: 1 } },
              redirect: '/projects/:project_id/label_sets/list/1',

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page?',
                  name: 'LabelSets',
                  meta: { label: $tk('route.label_set_list') },
                  props: (route) => {
                    return {
                      page: Number(route.params.page || 1)
                    };
                  },
                  component: LabelSets,
                },
                {
                  path: 'code',
                  name: 'LabelSetsCode',
                  meta: { label: $tk('route.label_set_code') },
                  component: CodeSnippets
                },
                {
                  path: 'create',
                  name: 'LabelSetCreate',
                  meta: { label: $tk('route.label_set_create_title') },
                  component: LabelSetForm
                },
                {
                  path: 'update/:id',
                  name: 'LabelSetUpdate',
                  meta: { label: $tk('route.label_set_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: LabelSetForm
                },
                {
                  path: ':label_set_id',
                  // redirect: '/projects/:project_id/sample_sets/list/1',
                  name: null,
                  // meta: { label: $tk('route.sample_set_list') },
                  props: (route) => {
                    return {
                      label_set_id: Number(route.params.label_set_id)
                    };
                  },

                  component: {
                    render(c) { return c('router-view'); }
                  },

                  children: [
                    {
                      path: 'samples',
                      name: 'AllLabelSetSamples',
                      meta: { label: $tk('route.label_set_samples_list_title') },
                      redirect: '/projects/:project_id/label_sets/:label_set_id/samples/list/1',

                      component: {
                        render(c) { return c('router-view'); }
                      },

                      children: [
                        {
                          path: 'list/:page?',
                          name: 'LabelSetSamples',
                          meta: { label: $tk('route.sample_list') },
                          props: (route) => {
                            return {
                              page: Number(route.params.page || 1),
                              label_set_id: Number(route.params.label_set_id)
                            };
                          },
                          component: LabelSetSamples,
                        },
                        {
                          path: 'label_sample/:sample_id',
                          name: 'LabelSample',
                          props: (route) => {
                            return {
                              sample_id: Number(route.params.sample_id),
                              label_set_id: Number(route.params.label_set_id),
                            };
                          },
                          meta: { label: $tk('route.label_object_set_title') },
                          component: ButtonPagination
                        },
                        // {
                        //   path: 'create',
                        //   name: 'SampleCreate',
                        //   meta: { label: $tk('route.sample_create_title') },
                        //   component: SampleForm,
                        //   props: (route) => {
                        //     return {
                        //       label_set_id: Number(route.params.label_set_id)
                        //     };
                        //   },
                        // },
                        // {
                        //   path: 'update/:id',
                        //   name: 'SampleUpdate',
                        //   meta: { label: $tk('route.sample_update_title') },
                        //   props: (route) => {
                        //     return {
                        //       id: Number(route.params.id),
                        //       labelSetId: Number(route.params.label_set_id)
                        //     };
                        //   },
                        //   component: SampleForm
                        // },
                      ]
                    },
                  ]
                }
              ]
            },
            {
              path: 'sample_sets',
              name: 'AllProjectSampleSets',
              meta: { label: $tk('route.sample_sets_list_title') },
              // redirect: { name: 'SampleSets', params: { page: 1 } },
              redirect: '/projects/:project_id/sample_sets/list/1',

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page?',
                  name: 'SampleSets',
                  meta: { label: $tk('route.sample_set_list') },
                  props: (route) => {
                    return {
                      page: Number(route.params.page || 1)
                    };
                  },
                  component: SampleSets,
                },
                {
                  path: 'create',
                  name: 'SampleSetCreate',
                  meta: { label: $tk('route.sample_set_create_title') },
                  component: SampleSetForm
                },
                {
                  path: 'update/:id',
                  name: 'SampleSetUpdate',
                  meta: { label: $tk('route.sample_set_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: SampleSetForm
                },
                {
                  path: 'delete/:id',
                  name: 'SampleSetDelete',
                  meta: { label: $tk('route.sample_set_delete_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: SampleSetDelete
                },
                {
                  path: 'upload/:id',
                  name: 'SampleSetUpload',
                  meta: { label: $tk('route.sample_set_upload_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: SampleSetUpload
                },
                {
                  path: ':sample_set_id',
                  // redirect: '/projects/:project_id/sample_sets/list/1',
                  name: null,
                  // meta: { label: $tk('route.sample_set_list') },
                  props: (route) => {
                    return {
                      sampleSetId: Number(route.params.sample_set_id)
                    };
                  },

                  component: {
                    render(c) { return c('router-view'); }
                  },

                  children: [
                    {
                      path: 'samples',
                      name: 'AllSampleSetSamples',
                      meta: { label: $tk('route.samples_list_title') },
                      redirect: '/projects/:project_id/sample_sets/:sample_set_id/samples/list/1',

                      component: {
                        render(c) { return c('router-view'); }
                      },

                      children: [
                        {
                          path: 'list/:page?',
                          name: 'Samples',
                          meta: { label: $tk('route.sample_list') },
                          props: (route) => {
                            return {
                              page: Number(route.params.page || 1),
                              sampleSetId: Number(route.params.sample_set_id)
                            };
                          },
                          component: Samples,
                        },
                        {
                          path: 'create',
                          name: 'SampleCreate',
                          meta: { label: $tk('route.sample_create_title') },
                          component: SampleForm,
                          props: (route) => {
                            return {
                              sampleSetId: Number(route.params.sample_set_id)
                            };
                          },
                        },
                        {
                          path: 'update/:id',
                          name: 'SampleUpdate',
                          meta: { label: $tk('route.sample_update_title') },
                          props: (route) => {
                            return {
                              id: Number(route.params.id),
                              sampleSetId: Number(route.params.sample_set_id)
                            };
                          },
                          component: SampleForm
                        },
                      ]
                    },
                  ]
                }

              ]
            },

            {
              path: 'detection_sets',
              name: null,
              meta: { label: $tk('route.detection_sets_list_title') },
              // redirect: { name: 'DetectionSets', params: { page: 1 } },
              redirect: '/projects/:project_id/detection_sets/list/1',
              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'DetectionSets',
                  meta: { label: $tk('route.detection_set_list') },
                  props: true,
                  component: DetectionSets,
                },
                {
                  path: 'create',
                  name: 'DetectionSetCreate',
                  meta: { label: $tk('route.detection_set_create_title') },
                  component: DetectionSetForm
                },
                {
                  path: 'update/:id',
                  name: 'DetectionSetUpdate',
                  meta: { label: $tk('route.detection_set_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: DetectionSetForm
                },
                {
                  path: 'delete/:id',
                  name: 'DetectionSetDelete',
                  meta: { label: $tk('route.detection_set_delete_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: DetectionSetDelete
                },
                {
                  path: 'upload/:id',
                  name: 'DetectionSetUpload',
                  meta: { label: $tk('route.detection_set_upload_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: DetectionSetUpload
                },
                {
                  path: ':detection_set_id',
                  // redirect: '/projects/:project_id/detection_sets/list/1',
                  name: null,
                  // meta: { label: $tk('route.detection_set_list') },
                  props: (route) => {
                    return {
                      detectionSetId: Number(route.params.detection_set_id)
                    };
                  },

                  component: {
                    render(c) { return c('router-view'); }
                  },

                  children: [
                    {
                      path: 'detections',
                      name: 'AllDetectionSetDetections',
                      meta: { label: $tk('route.detections_list_title') },
                      redirect: '/projects/:project_id/detection_sets/:detection_set_id/detections/list/1',

                      component: {
                        render(c) { return c('router-view'); }
                      },

                      children: [
                        {
                          path: 'list/:page?',
                          name: 'Detections',
                          meta: { label: $tk('route.detection_list') },
                          props: (route) => {
                            return {
                              page: Number(route.params.page || 1),
                              detectionSetId: Number(route.params.detection_set_id)
                            };
                          },
                          component: Detections,
                        },
                        {
                          path: 'create',
                          name: 'DetectionCreate',
                          meta: { label: $tk('route.detection_create_title') },
                          component: DetectionForm,
                          props: (route) => {
                            return {
                              detectionSetId: Number(route.params.detection_set_id)
                            };
                          },
                        },
                        {
                          path: 'update/:id',
                          name: 'DetectionUpdate',
                          meta: { label: $tk('route.detection_update_title') },
                          props: (route) => {
                            return {
                              id: Number(route.params.id),
                              detectionSetId: Number(route.params.detection_set_id)
                            };
                          },
                          component: DetectionForm
                        },
                      ]
                    },
                  ]
                },

              ]
            },
            {
              path: 'json_schemas',
              name: null,
              meta: { label: $tk('route.json_schemas_list_title') },
              redirect: '/projects/:project_id/json_schemas/list/1',

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'JsonSchemas',
                  meta: { label: $tk('route.json_schema_list') },
                  props: true,
                  component: JsonSchemas,
                },
                {
                  path: 'create',
                  name: 'JsonSchemaCreate',
                  meta: { label: $tk('route.json_schema_create_title') },
                  component: JsonSchemaForm
                },
                {
                  path: 'update/:id',
                  name: 'JsonSchemaUpdate',
                  meta: { label: $tk('route.json_schema_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: JsonSchemaForm
                },
              ]
            },
          ],
        },

        {
          path: 'dashboard',
          name: 'Dashboard',
          meta: { label: $tk('route.dashboard') },
          component: Dashboard
        },
        // ------- Administration----------------

        {
          path: '/administration',
          name: 'Administration',
          meta: { label: $tk('route.administration') },
          redirect: { name: 'Users', params: { page: 1 } },
          component: {
            render(c) { return c('router-view'); }
          },
          children: [

            {
              path: 'projects',
              // name: null,
              name: 'AllProjects',
              meta: { label: $tk('route.projects_list_title') },
              redirect: { name: 'Projects', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Projects',
                  meta: { label: $tk('route.project_list') },
                  props: true,
                  component: Projects,
                },
                {
                  path: 'create',
                  name: 'ProjectCreate',
                  meta: { label: $tk('route.project_create_title') },
                  component: ProjectForm
                },
                {
                  path: 'update/:id',
                  name: 'ProjectUpdate',
                  meta: { label: $tk('route.project_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: ProjectForm
                },
                {
                  path: 'export/:id',
                  name: 'ProjectExport',
                  meta: { label: $tk('route.project_export_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: ProjectExportation
                },

              ]
            },

            {
              path: 'users',
              name: null,
              meta: { label: $tk('route.users_list_title') },
              redirect: { name: 'Users', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page?',
                  name: 'Users',
                  meta: { label: $tk('route.users_list') },
                  props: true,
                  component: Users,
                },
                {
                  path: 'create',
                  name: 'UserCreate',
                  meta: { label: $tk('route.user_create_title') },
                  component: UserCreate
                },
                {
                  path: 'update/:id',
                  name: 'UserUpdate',
                  meta: { label: $tk('route.user_update_title') },
                  props: true,
                  component: UserUpdate
                },
              ]
            },
            {
              path: 'roles',
              name: null,
              meta: { label: $tk('route.roles_list_title') },
              redirect: { name: 'Roles', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Roles',
                  meta: { label: $tk('route.role_list') },
                  props: true,
                  component: Roles,
                },
                {
                  path: 'create',
                  name: 'RoleCreate',
                  meta: { label: $tk('route.roles_create_title') },
                  component: RoleCreate
                },
                {
                  path: 'update/:id',
                  name: 'RoleUpdate',
                  meta: { label: $tk('route.role_update_title') },
                  props: true,
                  component: RoleUpdate
                },
              ]
            },
            {
              path: 'audit-logs',
              name: null,
              meta: { label: $tk('route.audit_logs_list_title') },
              redirect: { name: 'AuditLogs', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'AuditLogs',
                  meta: { label: $tk('route.auditlogs_list') },
                  props: true,
                  component: AuditLogs,
                },
              ]
            },

            {
              path: 'translations',
              name: null,
              meta: { label: $tk('route.translations_list_title') },
              redirect: { name: 'Translations', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Translations',
                  meta: { label: $tk('route.translations_list') },
                  props: true,
                  component: Translations,
                },
                {
                  path: 'create',
                  name: 'TranslationCreate',
                  meta: { label: $tk('route.translation_create_title') },
                  component: TranslationForm
                },
                {
                  path: 'update/:id',
                  name: 'TranslationUpdate',
                  meta: { label: $tk('route.translation_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: TranslationForm
                },
              ]
            },

            {
              path: 'languages',
              name: null,
              meta: { label: $tk('route.languages_list_title') },
              redirect: { name: 'Languages', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Languages',
                  meta: { label: $tk('route.languages_list') },
                  props: true,
                  component: Languages,
                },
                {
                  path: 'create',
                  name: 'LanguageCreate',
                  meta: { label: $tk('route.language_create_title') },
                  component: LanguageForm
                },
                {
                  path: 'update/:id',
                  name: 'LanguageUpdate',
                  meta: { label: $tk('route.language_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: LanguageForm
                },
              ]
            },

            {
              path: 'notifications',
              name: null,
              meta: { label: $tk('route.notifications_list_title') },
              redirect: { name: 'Notifications', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Notifications',
                  meta: { label: $tk('route.notifications_list') },
                  props: true,
                  component: Notifications,
                },
              ]
            },
          ]
        },
        {
          path: '/user-profile',
          name: 'UserProfile',
          meta: { label: $tk('route.user_profile_title') },
          redirect: '/user-profile/view',

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'view',
              name: 'UserProfileView',
              meta: { label: $tk('route.user_profile_view_title') },
              component: UserProfile
            },
            {
              path: 'api-token-create',
              name: 'ApiTokenCreate',
              meta: { label: $tk('route.api_token_create_title') },
              component: ApiTokenForm
            },
          ]
        },

      ]
    },
    {
      path: '/login',
      name: 'Login',
      meta: { label: $tk('route.login') },
      component: Login
    },
    {
      path: '/register',
      name: 'Register',
      meta: { label: $tk('route.register') },
      component: Register
    },
    {
      path: '/password-forgot',
      name: 'PasswordForgot',
      meta: { label: $tk('route.forgot_password') },
      component: PasswordForgot
    },
    {
      path: '/password-reset/:token',
      props: true,
      name: 'PasswordReset',
      meta: { label: $tk('route.password_reset') },
      component: PasswordReset
    },
  ]
});

router.beforeEach((to, from, next) => {
  if (!publicRoutes.includes(to.name) && !store.getters['Auth/isLoggedIn']) {
    next({ name: 'Login' });
    return;
  }
  if (store.getters['Auth/isLoggedIn'] && ['Login', 'Register', 'PasswordForgot', 'PasswordReset'].includes(to.name)) {
    next({ name: 'Dashboard' });
    return;
  }
  if (to.name === 'Login' && from.name && from.name !== 'Login'
    && from.name !== 'Register'
    && from.name !== 'PasswordForgot'
    && from.name !== 'PasswordReset') {
    sessionStorage.setItem('redirectPath', from.path);
    next();
    return;
  }
  // change active project by project id in url
  if (to?.params?.project_id && parseInt(to.params.project_id, 10)) {
    const paramsProjectId = parseInt(to.params.project_id, 10);
    if (store.state.Auth.activeProjectId !== paramsProjectId) {
      store.commit(VTN.SET_ACTIVE_PROJECT, paramsProjectId);
    }
  }

  next();
});

export default router;
